import { Button, DatePicker, Flex, Input, Typography } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ContentType } from '@pages/Clients/NewClientsList/components/MultiFilter/types';

import { useNewClientsListContext } from '@pages/Clients/NewClientsList/context/useNewClientsListContext';
import { PF } from '@pages/Clients/NewClientsList/data/constants';
import { FilterWrapper } from '@pages/Clients/NewClientsList/ui/FilterWrapper';

import type { NoUndefinedRangeValueType } from 'rc-picker/lib/PickerInput/RangePicker';

const { RangePicker } = DatePicker;
const { Text } = Typography;

type DayjsRange = NoUndefinedRangeValueType<dayjs.Dayjs>;

interface QuickButton {
  label: string;
  to: dayjs.Dayjs;
}

const QUICK_BUTTONS: QuickButton[] = [
  { label: 'Сегодня', to: dayjs() },
  { label: 'Завтра', to: dayjs().add(1, 'day') },
  { label: 'Текущая неделя', to: dayjs().add(7, 'day') },
  { label: 'Следующий месяц', to: dayjs().add(1, 'month') },
  { label: 'Следующие 3 месяца', to: dayjs().add(3, 'month') },
];

const MIN_FUTURE_DAYS = 1;

const getCurrentWeekPeriod = (): DayjsRange => {
  const today = dayjs();
  const dayOfWeek = today.day();
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const monday = today.subtract(daysFromMonday, 'day').startOf('day');
  const sunday = monday.add(6, 'day').endOf('day');

  return [monday, sunday];
};

const getQuickButtonPeriod = (label: string, to: dayjs.Dayjs): DayjsRange => {
  if (label === 'Завтра') {
    return [
      dayjs().add(1, 'day').startOf('day'),
      dayjs().add(1, 'day').endOf('day'),
    ];
  }

  if (label === 'Текущая неделя') {
    return getCurrentWeekPeriod();
  }

  return [dayjs(), to];
};

export const BirthdayFilter: ContentType = ({ open, onClose, id }) => {
  const { filtersMap, onChangeBirthdayPeriod } = useNewClientsListContext();

  const birthdayPeriod = filtersMap.get(id)?.birthdayPeriod ?? null;

  const [localPeriod, setLocalPeriod] = useState<DayjsRange | null>(null);
  const [localFutureDays, setLocalFutureDays] = useState<number | undefined>(
    undefined
  );

  const onConfirm = useCallback(() => {
    onChangeBirthdayPeriod(localPeriod, id);
    onClose?.();
  }, [id, localPeriod, onChangeBirthdayPeriod, onClose]);

  useEffect(() => {
    if (!open) {
      setLocalPeriod(null);
      setLocalFutureDays(undefined);
      return;
    }

    setLocalPeriod(birthdayPeriod);
    setLocalFutureDays(undefined);
  }, [open, birthdayPeriod]);

  useEffect(() => {
    if (!open) return;
    if (localFutureDays === undefined) return;

    setLocalPeriod([dayjs(), dayjs().add(localFutureDays, 'day')]);
  }, [localFutureDays, open]);

  const rangePickerValue = useMemo(() => localPeriod, [localPeriod]);

  const parsePositiveIntOrUndefined = useCallback((raw: string) => {
    const digitsOnly = raw.replace(/[^\d]/g, '');
    if (digitsOnly.length === 0) return undefined;

    const parsed = Number(digitsOnly);
    if (!Number.isFinite(parsed)) return undefined;

    const floored = Math.floor(parsed);
    return floored < MIN_FUTURE_DAYS ? MIN_FUTURE_DAYS : floored;
  }, []);

  const handleFutureDaysKeyDown = useCallback<
    NonNullable<React.ComponentProps<typeof Input>['onKeyDown']>
  >((e) => {
    if (
      e.key === '-' ||
      e.key === '+' ||
      e.key === 'e' ||
      e.key === 'E' ||
      e.key === '.' ||
      e.key === ','
    ) {
      e.preventDefault();
    }
  }, []);

  const handleFutureDaysChange = useCallback<
    NonNullable<React.ComponentProps<typeof Input>['onChange']>
  >(
    (e) => {
      const next = parsePositiveIntOrUndefined(e.target.value);

      if (next === undefined) {
        setLocalFutureDays(undefined);
        return;
      }

      setLocalFutureDays(next);
    },
    [parsePositiveIntOrUndefined]
  );

  const futureDaysInputValue = useMemo(() => {
    return localFutureDays === undefined ? '' : String(localFutureDays);
  }, [localFutureDays]);

  const handleQuickButtonClick = (label: string, to: dayjs.Dayjs) => {
    setLocalFutureDays(undefined);
    setLocalPeriod(getQuickButtonPeriod(label, to));
  };

  return (
    <FilterWrapper onConfirm={onConfirm}>
      <Flex vertical gap={8}>
        <RangePicker
          format="DD.MM"
          allowEmpty={[true, true]}
          placeholder={['Дата с', 'Дата по']}
          value={rangePickerValue}
          onChange={(dates) => {
            setLocalFutureDays(undefined);
            setLocalPeriod(dates);
          }}
          className={`${PF}__range-picker`}
        />

        <Flex gap={4} vertical>
          {QUICK_BUTTONS.map(({ label, to }) => {
            const [expectedFrom, expectedTo] = getQuickButtonPeriod(label, to);
            const isSelected =
              localPeriod != null &&
              localPeriod[0]?.isSame(expectedFrom, 'day') &&
              localPeriod[1]?.isSame(expectedTo, 'day');

            return (
              <Button
                key={label}
                size="small"
                type={isSelected ? 'primary' : 'text'}
                onClick={() => handleQuickButtonClick(label, to)}
              >
                {label}
              </Button>
            );
          })}

          <Flex gap={8} align="center">
            <Text>Следующие</Text>
            <Input
              size="small"
              style={{ width: 100 }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={futureDaysInputValue}
              onKeyDown={handleFutureDaysKeyDown}
              onChange={handleFutureDaysChange}
            />
            <Text>дней</Text>
          </Flex>
        </Flex>
      </Flex>
    </FilterWrapper>
  );
};
